import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getDashboardNotifications,
  getDashboardNotificationUnreadCount,
  markDashboardNotificationRead,
  markAllDashboardNotificationsRead,
} from '../../services/dashboard.service';
import { resolveNotificationTargetPath } from '../utils/notificationNavigation';

function formatNotificationRelativeTime(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const abs = Math.abs(diffMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (abs < minute) return 'Just now';
  if (abs < hour) {
    const mins = Math.max(1, Math.round(abs / minute));
    return `${mins}m ago`;
  }
  if (abs < day) {
    const hours = Math.max(1, Math.round(abs / hour));
    return `${hours}h ago`;
  }
  if (abs < 7 * day) {
    const days = Math.max(1, Math.round(abs / day));
    return `${days}d ago`;
  }
  try {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export function getNotificationKind(notification) {
  const raw = notification && typeof notification === 'object' ? notification : {};
  const eventType = String(raw.sourceEventType || raw.type || '').trim().toUpperCase();
  const sourceType = String(raw.sourceType || '').trim().toLowerCase();
  const text = `${raw.title || ''} ${raw.message || ''}`.toLowerCase();
  const severity = String(raw.severity || 'info').trim().toLowerCase();

  if (eventType.includes('SCANNER') || sourceType === 'scanner' || /\bscanner\b/.test(text)) {
    return { kind: 'scanner', label: 'Scanner', severity };
  }
  if (
    eventType.includes('VULNERABILITY') ||
    sourceType === 'vulnerability' ||
    (/\bvulnerabilit/.test(text) && !/no vulnerabilit/.test(text))
  ) {
    return { kind: 'risk', label: 'Risk', severity: severity === 'info' ? 'warning' : severity };
  }
  if (eventType.includes('SUPPORT') || sourceType === 'support' || /\bticket\b|\bsupport\b/.test(text)) {
    return { kind: 'support', label: 'Support', severity };
  }
  if (
    eventType.includes('EARLY') ||
    eventType.includes('GHOST') ||
    eventType.includes('DISCLOSURE') ||
    /early disclosure|ghost cve/.test(text)
  ) {
    return { kind: 'intel', label: 'Intel', severity };
  }
  if (eventType.includes('SCAN') || sourceType === 'scan' || /\bscan\b/.test(text)) {
    return { kind: 'scan', label: 'Scan', severity };
  }
  return { kind: 'general', label: 'Alert', severity };
}

export function useNotifications({ navigate }) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0);
  const [markAllBusy, setMarkAllBusy] = useState(false);

  const unreadCount = useMemo(() => {
    const fromApi = Number(notificationsUnreadCount);
    if (Number.isFinite(fromApi)) return Math.max(0, Math.floor(fromApi));
    return notifications.filter((n) => !n.read).length;
  }, [notifications, notificationsUnreadCount]);

  const loadNotifications = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setNotificationsLoading(true);
    setNotificationsError('');
    try {
      const payload = await getDashboardNotifications();
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setNotifications(items);
      const unread = Number(payload?.unreadCount);
      if (Number.isFinite(unread)) {
        setNotificationsUnreadCount(Math.max(0, Math.floor(unread)));
      } else {
        setNotificationsUnreadCount(items.filter((item) => !item.read).length);
      }
    } catch {
      setNotifications([]);
      setNotificationsUnreadCount(0);
      setNotificationsError('Unable to load notifications right now.');
    } finally {
      if (!silent) setNotificationsLoading(false);
    }
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const payload = await getDashboardNotificationUnreadCount();
      const unread = Number(payload?.unreadCount);
      setNotificationsUnreadCount(Number.isFinite(unread) ? Math.max(0, Math.floor(unread)) : 0);
    } catch {
      // Keep optimistic count when unread-count refresh is unavailable.
    }
  }, []);

  const handleNotificationClick = useCallback(async (notification) => {
    const id = String(notification?.id || '').trim();

    if (id && !notification.read) {
      setNotifications((prev) =>
        prev.map((item) => (String(item?.id || '') === id ? { ...item, read: true } : item))
      );
      setNotificationsUnreadCount((prev) => Math.max(0, Number(prev || 0) - 1));

      try {
        await markDashboardNotificationRead(id);
        await refreshUnreadCount();
      } catch {
        setNotificationsError('Unable to update notification right now.');
      }
    }

    const targetLink = resolveNotificationTargetPath(notification);
    if (targetLink) {
      if (targetLink.startsWith('/')) {
        navigate(targetLink);
      } else if (typeof window !== 'undefined') {
        window.location.href = targetLink;
      }
    }

    setShowNotifications(false);
  }, [navigate, refreshUnreadCount]);

  const handleMarkAllRead = useCallback(async () => {
    if (markAllBusy || unreadCount <= 0) return;
    setMarkAllBusy(true);
    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
    setNotificationsUnreadCount(0);
    try {
      await markAllDashboardNotificationsRead();
      await refreshUnreadCount();
      await loadNotifications({ silent: true });
    } catch {
      setNotificationsError('Unable to mark notifications as read.');
      await loadNotifications({ silent: true });
    } finally {
      setMarkAllBusy(false);
    }
  }, [loadNotifications, markAllBusy, refreshUnreadCount, unreadCount]);

  useEffect(() => {
    loadNotifications({ silent: true });
  }, [loadNotifications]);

  // Light poll for badge freshness while logged into the dashboard shell.
  useEffect(() => {
    const timer = setInterval(() => {
      refreshUnreadCount();
      if (showNotifications) {
        loadNotifications({ silent: true });
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [loadNotifications, refreshUnreadCount, showNotifications]);

  useEffect(() => {
    if (!showNotifications) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setShowNotifications(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showNotifications]);

  const notificationItems = useMemo(
    () =>
      notifications.map((item) => {
        const meta = getNotificationKind(item);
        const title = String(item.title || meta.label || 'Notification').trim() || 'Notification';
        const message = String(item.message || '').trim();
        const body = message && message !== title ? message : '';
        return {
          ...item,
          ...meta,
          displayTitle: title,
          displayBody: body,
          relativeTime: formatNotificationRelativeTime(item.createdAt),
          hasTarget: Boolean(resolveNotificationTargetPath(item)),
        };
      }),
    [notifications]
  );

  return {
    showNotifications,
    setShowNotifications,
    notifications: notificationItems,
    notificationsLoading,
    notificationsError,
    unreadCount,
    markAllBusy,
    loadNotifications,
    handleNotificationClick,
    handleMarkAllRead,
  };
}
